// parityRegistry.ts — KANONICZNY rejestr zdolności odczytu starego panelu.
//
// ══════════════════════════════════════════════════════════════════════════════════════════════
//  PO CO REJESTR W KODZIE, A NIE TYLKO W DOKUMENCIE
//
//  Poprzedni licznik brzmiał `READ_PARITY = 0/13` i miał DWA defekty, których dokument
//  nie potrafił złapać:
//
//    1. ZŁY MIANOWNIK — O5 i O13 opisują TĘ SAMĄ zdolność (ten sam kod źródłowy:
//       RestaurantController.cs:249-273). Jedna funkcja liczona dwa razy.
//    2. ZLEPIONE WYMIARY — jeden procent mieszał „czy Control Center ma odpowiednik"
//       z „czy liczby są zweryfikowane", więc nie odpowiadał na żadne z tych pytań.
//
//  Rejestr w kodzie sprawia, że oba defekty są WYKRYWALNE TESTEM, a nie zależą od tego,
//  czy ktoś uważnie przeczytał tabelę w markdownie.
//
//  Decyzje klasyfikacyjne: ADR-004 (granica wygaszania panelu).
// ══════════════════════════════════════════════════════════════════════════════════════════════

/** Klasa zdolności — rozdzielona, bo miesza dwie różne role i dwa różne programy (ADR-004). */
export type CapabilityClass =
  /** Analityka i rejestry, rola ADMIN. To jest zakres R5-15. */
  | 'ANALYTICS'
  /** Operacje restauracji, rola DELIVER (arkusz produkcji, wydruki, lista zamówień).
   *  POZA zakresem R5-15 — Control Center nie dostaje za to FAIL. */
  | 'OPERATIONAL';

/** Statusy dopuszczone przez PM. Innych nie wolno wprowadzać bez zmiany ADR. */
export type ParityStatus =
  | 'VERIFIED_EQUAL'
  | 'VERIFIED_SEMANTICALLY_EQUIVALENT'
  | 'SEMANTIC_MISMATCH'
  /** Rozjazd ROZSTRZYGNIĘTY: liczby różnią się celowo i wiadomo dlaczego. To nie jest
   *  „jeszcze niesprawdzone" ani usterka do naprawienia — to udokumentowana decyzja.
   *  Wolno go nadać dopiero wtedy, gdy przyczyna jest nazwana w polu `uwaga`. */
  | 'SEMANTIC_MISMATCH_BY_DESIGN'
  /** Rozstrzygnięte i WDROŻONE: Control Center liczy zgodnie z przyjętą regułą kanoniczną.
   *  Różnica wobec panelu legacy wynika z jego własnej niespójności, nie z naszego długu. */
  | 'FIXED_TO_CANONICAL'
  | 'NO_LEGACY_EQUIVALENT'
  | 'BLOCKED_BY_DATA'
  | 'NOT_TESTED';

export interface Capability {
  /** Identyfikator STABILNY. Nie przenumerowujemy — to unieważniłoby wcześniejsze raporty. */
  id: string;
  name: string;
  klasa: CapabilityClass;
  status: ParityStatus;
  /** Identyfikator wycofany, opisujący TĘ SAMĄ zdolność. Zachowany jawnie, nie skasowany. */
  aliasWycofany?: string;
  /** Czy Control Center ma funkcjonalny odpowiednik (niezależnie od zgodności liczb). */
  maOdpowiednikWCC: boolean;
  uwaga?: string;

  /**
   * KTO odpowiada za domknięcie tej zdolności.
   *
   * Pole istnieje, bo „zdolność bez właściciela to notatka, nie zadanie". Rejestr, w którym
   * nikt nie jest przypisany do zablokowanej pozycji, wygląda jak plan, a działa jak lista
   * życzeń — i po miesiącu nikt nie pamięta, czy ktoś się tym w ogóle zajmuje.
   */
  wlasciciel: Wlasciciel;

  /**
   * CO SIĘ STANIE ZE STARYM PANELEM, jeśli ta zdolność nie zostanie domknięta.
   *
   * To jest jedyne pole, które łączy rejestr z decyzją biznesową. Bez niego lista statusów
   * odpowiada na pytanie „ile zrobiliśmy", a nie na pytanie, które naprawdę pada:
   * „czy możemy wyłączyć stary panel".
   */
  wplywNaWygaszenie: WplywNaWygaszenie;
}

/** Kto domyka zdolność. Świadomie wąska lista — „ktoś" nie jest właścicielem. */
export type Wlasciciel =
  /** Program R5-15 — Control Center & Intelligence. */
  | 'R5-15'
  /** Poza zakresem R5-15; wymaga innego strumienia albo decyzji właściciela firmy. */
  | 'POZA_R5-15'
  /** Zablokowane brakiem danych, których nikt w R5-15 nie może dostarczyć samodzielnie. */
  | 'CZEKA_NA_DANE';

export type WplywNaWygaszenie =
  /** Bez tej zdolności NIE da się wyłączyć starego panelu. */
  | 'BLOKUJE_WYGASZENIE'
  /** Panel dałoby się wyłączyć, ale administrator straciłby wygodę albo widok pomocniczy. */
  | 'UTRUDNIA'
  /** Nie ma wpływu na decyzję o wygaszeniu. */
  | 'BEZ_WPLYWU';

export const OPIS_WLASCICIELA: Record<Wlasciciel, string> = {
  'R5-15': 'R5-15',
  'POZA_R5-15': 'poza zakresem R5-15',
  CZEKA_NA_DANE: 'czeka na dane',
};

export const OPIS_WPLYWU: Record<WplywNaWygaszenie, string> = {
  BLOKUJE_WYGASZENIE: 'blokuje wygaszenie panelu',
  UTRUDNIA: 'utrudnia, nie blokuje',
  BEZ_WPLYWU: 'bez wpływu na wygaszenie',
};

export const CAPABILITIES: readonly Capability[] = [
  {
    id: 'O1',
    name: 'Rejestr transakcji finansowych (pojedyncza płatność ucznia)',
    klasa: 'ANALYTICS',
    status: 'NO_LEGACY_EQUIVALENT',
    /*
     * WPIS ZAKTUALIZOWANY. Do R5-15-011 stał tu opis sprzed R5-15-007: „brak w Control Center
     * na wszystkich warstwach — nie ma trasy, endpointu ani pozycji" oraz „blokuje wygaszenie".
     * Oba zdania przestały być prawdziwe w dniu dowiezienia O1, ale nikt ich nie zmienił,
     * bo nic się przez to nie zepsuło. Rejestr jest źródłem dla ekranu Gotowość, więc
     * nieaktualny wpis oznaczał, że produkt pokazywał właścicielowi nieprawdę.
     *
     * Wpis był przy okazji WEWNĘTRZNIE SPRZECZNY: nota mówiła „blokuje wygaszenie", a pole
     * wplywNaWygaszenie miało wartość BEZ_WPLYWU. Test niżej pilnuje teraz, żeby nota i pole
     * nie mogły się rozjechać.
     *
     * DOWÓD, że O1 istnieje w Control Center:
     *   backend  — crm/backend/routes/transactions.js (GET /transactions, /transactions/:id)
     *   ekran    — src/workspaces/crm/TransactionsPage.tsx
     *   trasa    — /crm/transakcje (app/router.tsx)
     *   E2E      — test 16 na publicznym TEST
     */
    maOdpowiednikWCC: true,
    wlasciciel: 'R5-15',
    wplywNaWygaszenie: 'BEZ_WPLYWU',
    uwaga:
      'Miejsce obsługi reklamacji: rozdziela wartość wg cennika i kwotę faktycznie pobraną, ' +
      'a braku nie zamienia na zero. Dowieziony w R5-15-007, potwierdzony publicznym E2E. ' +
      'Nie blokuje wygaszenia — Control Center ma tę zdolność.',
  },
  { id: 'O2', name: 'Sprzedaż per szkoła', klasa: 'ANALYTICS', status: 'SEMANTIC_MISMATCH', maOdpowiednikWCC: true, wlasciciel: 'R5-15', wplywNaWygaszenie: 'BLOKUJE_WYGASZENIE' },
  { id: 'O3', name: 'Sprzedaż per restauracja', klasa: 'ANALYTICS', status: 'SEMANTIC_MISMATCH', maOdpowiednikWCC: true, wlasciciel: 'R5-15', wplywNaWygaszenie: 'BLOKUJE_WYGASZENIE' },
  {
    id: 'O4',
    name: 'Dzienny arkusz produkcji restauracji',
    klasa: 'OPERATIONAL',
    status: 'NO_LEGACY_EQUIVALENT',
    maOdpowiednikWCC: false,
    wlasciciel: 'POZA_R5-15',
    wplywNaWygaszenie: 'BLOKUJE_WYGASZENIE',
    uwaga: 'Wymaga DailyPosition.Count, którego CRM w ogóle nie synchronizuje.',
  },
  {
    id: 'O5',
    name: 'Wydruk arkusza produkcji i etykiet (PDF)',
    klasa: 'OPERATIONAL',
    status: 'NO_LEGACY_EQUIVALENT',
    maOdpowiednikWCC: false,
    wlasciciel: 'POZA_R5-15',
    wplywNaWygaszenie: 'BLOKUJE_WYGASZENIE',
    aliasWycofany: 'O13',
    uwaga: 'O13 opisywał TĘ SAMĄ zdolność — ten sam kod (RestaurantController.cs:249-273).',
  },
  {
    id: 'O6',
    name: 'Lista zamówień restauracji',
    klasa: 'OPERATIONAL',
    status: 'NO_LEGACY_EQUIVALENT',
    maOdpowiednikWCC: false,
    wlasciciel: 'POZA_R5-15',
    wplywNaWygaszenie: 'BLOKUJE_WYGASZENIE',
    // Nota dopisana, bo test słusznie wytknął jej brak: status NO_LEGACY_EQUIVALENT znaczy co
    // innego dla zdolności analitycznej („panel tego nie ma") niż dla operacyjnej („to nie jest
    // raport, tylko narzędzie pracy"). Sam status jest tu dwuznaczny i bez noty nie do użycia.
    uwaga:
      'Operacyjna lista zamówień restauratora — narzędzie pracy, nie raport. R5-15 świadomie ' +
      'jej nie implementuje: to zakres operacji dostawczych. Blokuje wygaszenie panelu, ' +
      'bo restaurator nie ma dokąd pójść po tę funkcję.',
  },
  {
    id: 'O7',
    name: 'Rozkład sprzedaży w czasie',
    klasa: 'ANALYTICS',
    // ZAMKNIĘTE. Wcześniej ten wpis mówił, że rozjazd „wymaga decyzji, którą oś uznajemy za
    // wiążącą". Decyzja zapadła i jest kanoniczna:
    //   WYNIKI BIZNESOWE      → dzień dostawy  (DELIVERY_DATE)
    //   ZACHOWANIE ZAKUPOWE   → dzień zakupu   (ORDER_DATE)
    // Control Center już tak liczy — `TimeAxis` w lib/metricBasis.ts nie dopuszcza innej wartości,
    // a każdy ekran podaje oś przy liczbie. Panel legacy pozostaje wewnętrznie niespójny (okno
    // tnie po dacie dostawy, wykres kubełkuje po dacie zakupu) i tej niespójności NIE kopiujemy.
    // Rozjazd wobec panelu zostaje więc z PROJEKTU, a nie z braku rozstrzygnięcia.
    status: 'FIXED_TO_CANONICAL',
    maOdpowiednikWCC: true,
    wlasciciel: 'R5-15',
    wplywNaWygaszenie: 'UTRUDNIA',
    uwaga:
      'Zamknięte regułą kanoniczną: wyniki biznesowe liczymy dniem dostawy, zachowanie zakupowe ' +
      'dniem zakupu. Control Center liczy zgodnie z regułą i podaje oś przy każdej liczbie. ' +
      'Różnica wobec panelu legacy jest skutkiem jego wewnętrznej niespójności, nie naszej ' +
      'nierozstrzygniętej decyzji — dlatego to NIE jest dług do spłacenia.',
  },
  {
    id: 'O8',
    name: 'Struktura sprzedaży restauratora',
    klasa: 'ANALYTICS',
    status: 'SEMANTIC_MISMATCH_BY_DESIGN',
    maOdpowiednikWCC: true,
    wlasciciel: 'R5-15',
    wplywNaWygaszenie: 'UTRUDNIA',
    uwaga:
      'Rozjazd jest STRUKTURALNY. Ekran rozbija sprzedaż na dania, a kwota pobrana nie istnieje ' +
      'na ziarnie pozycji — jest wyłącznie na transakcji. Pokazanie jej w rozbiciu wymagałoby ' +
      'rozdzielenia kwoty transakcji na pozycje, czyli zmyślenia liczby. Dlatego Control Center ' +
      'podaje tu wartość katalogową i mówi to wprost, zamiast udawać przychód.',
  },
  {
    id: 'O9',
    name: 'Rejestr ocen i reklamacji',
    klasa: 'ANALYTICS',
    status: 'NO_LEGACY_EQUIVALENT',
    maOdpowiednikWCC: false,
    wlasciciel: 'CZEKA_NA_DANE',
    wplywNaWygaszenie: 'UTRUDNIA',
    uwaga: 'Brak tabeli crm_ratings i brak kanału ingest — czterowarstwowe zero.',
  },
  { id: 'O10', name: 'Słownik szkół i miast', klasa: 'ANALYTICS', status: 'SEMANTIC_MISMATCH', maOdpowiednikWCC: true, wlasciciel: 'R5-15', wplywNaWygaszenie: 'BLOKUJE_WYGASZENIE' },
  { id: 'O11', name: 'Słownik restauracji i katalog dań', klasa: 'ANALYTICS', status: 'SEMANTIC_MISMATCH', maOdpowiednikWCC: true, wlasciciel: 'R5-15', wplywNaWygaszenie: 'BLOKUJE_WYGASZENIE' },
  {
    id: 'O12',
    name: 'Rejestr kont i portfeli',
    klasa: 'ANALYTICS',
    status: 'NO_LEGACY_EQUIVALENT',
    maOdpowiednikWCC: false,
    wlasciciel: 'CZEKA_NA_DANE',
    wplywNaWygaszenie: 'BLOKUJE_WYGASZENIE',
    uwaga: 'Formuła salda w CRM jest identyczna jak w panelu; blokadą jest ADR tożsamości, nie obliczenia.',
  },
];

export interface ParitySummary {
  unikalnychZdolnosci: number;
  analityczne: number;
  operacyjne: number;
  maOdpowiednik: number;
  semanticMismatch: number;
  brakOdpowiednika: number;
  mismatchZProjektu: number;
  zamknieteKanonicznie: number;
  zweryfikowaneLiczbowo: number;
  zablokowaneBrakiemDanych: number;
  aliasyWycofane: string[];
}

/**
 * Wielowymiarowy licznik. ŚWIADOMIE nie zwraca jednego procenta —
 * pojedynczy KPI mieszał trzy pytania i przez to nie odpowiadał na żadne (ADR-004).
 */
export function podsumujParytet(caps: readonly Capability[] = CAPABILITIES): ParitySummary {
  const analityczne = caps.filter((c) => c.klasa === 'ANALYTICS');
  return {
    unikalnychZdolnosci: caps.length,
    analityczne: analityczne.length,
    operacyjne: caps.filter((c) => c.klasa === 'OPERATIONAL').length,
    maOdpowiednik: analityczne.filter((c) => c.maOdpowiednikWCC).length,
    semanticMismatch: analityczne.filter((c) => c.status === 'SEMANTIC_MISMATCH').length,
    // Rozjazdy ROZSTRZYGNIĘTE liczymy osobno. Wrzucenie ich do jednego worka z nierozstrzygniętymi
    // kazałoby czytać ten sam licznik raz jako dług, raz jako decyzję.
    mismatchZProjektu: analityczne.filter((c) => c.status === 'SEMANTIC_MISMATCH_BY_DESIGN').length,
    // Zdolności ZAMKNIĘTE regułą kanoniczną. Liczone osobno, bo to nie jest ani zgodność
    // liczbowa z panelem, ani otwarty rozjazd — to rozstrzygnięcie, po którym nie ma już pracy.
    zamknieteKanonicznie: analityczne.filter((c) => c.status === 'FIXED_TO_CANONICAL').length,
    brakOdpowiednika: analityczne.filter((c) => c.status === 'NO_LEGACY_EQUIVALENT').length,
    // Równość liczbowa wymaga niepustej sprzedaży — dziś nieosiągalna, i to jest stan, nie błąd.
    zweryfikowaneLiczbowo: analityczne.filter((c) => c.status === 'VERIFIED_EQUAL').length,
    zablokowaneBrakiemDanych: analityczne.filter((c) => c.maOdpowiednikWCC).length,
    aliasyWycofane: caps.flatMap((c) => (c.aliasWycofany ? [c.aliasWycofany] : [])),
  };
}

/** Czy panel można wygasić. Sama gotowa analityka NIE wystarcza (ADR-004). */
export function czyMoznaWygasicPanel(caps: readonly Capability[] = CAPABILITIES): boolean {
  return caps.every((c) => c.maOdpowiednikWCC);
}

/* ══════════════════════════════════════════════════════════════════════════════════════════════
   DWA PYTANIA WŁAŚCICIELA — ODPOWIEDZI WYPROWADZONE Z REJESTRU
   ══════════════════════════════════════════════════════════════════════════════════════════════

   Te odpowiedzi MUSZĄ być liczone, a nie deklarowane w ekranie. Zdanie „panelu nie można jeszcze
   wyłączyć" wpisane w JSX przestaje być prawdziwe w dniu, w którym ostatnia blokada zniknie —
   i nikt tego nie zauważy, bo nic się nie zepsuje. Odwrotnie jest jeszcze gorzej: ekran
   ogłaszający gotowość, której rejestr nie potwierdza, to zaproszenie do wyłączenia narzędzia,
   z którego ktoś nadal korzysta.

   Pytania są DWA i nie wolno ich mieszać:
     A. Czy administrator może przestać używać starego panelu W SWOJEJ PRACY?
     B. Czy panel można wyłączyć DLA WSZYSTKICH?
   Odpowiedź A może brzmieć „tak" przy odpowiedzi B „nie" — i dziś dokładnie tak jest.
   ══════════════════════════════════════════════════════════════════════════════════════════════ */

export type OdpowiedzAdmina = 'TAK' | 'CZESCIOWO' | 'NIE';

/** Zdolności, których brak realnie blokuje wygaszenie panelu. */
export function blokadyWygaszenia(caps: readonly Capability[] = CAPABILITIES): Capability[] {
  return caps.filter((c) => c.wplywNaWygaszenie === 'BLOKUJE_WYGASZENIE' && !c.maOdpowiednikWCC);
}

/**
 * A. Czy ADMINISTRATOR może przestać używać starego panelu.
 * Liczy się tylko to, co administrator robi — zdolności klasy ANALYTICS. Operacje kierowców
 * nie są jego pracą i nie mogą decydować o tej odpowiedzi.
 */
export function czyAdminMozeOdejsc(caps: readonly Capability[] = CAPABILITIES): OdpowiedzAdmina {
  const adminowe = caps.filter((c) => c.klasa === 'ANALYTICS');
  const bezOdpowiednika = adminowe.filter((c) => !c.maOdpowiednikWCC);
  if (bezOdpowiednika.length === 0) return 'TAK';
  // Brakuje czegoś, ale nie wszystkiego — administrator może pracować w Control Center
  // i wracać do panelu tylko po te pozycje.
  return bezOdpowiednika.length < adminowe.length ? 'CZESCIOWO' : 'NIE';
}

/**
 * B. Czy panel można wyłączyć GLOBALNIE.
 * Wystarczy JEDNA blokada, żeby odpowiedź brzmiała „nie" — i tak ma być. Wygaszenie jest
 * decyzją nieodwracalną w praktyce: po wyłączeniu nikt nie sprawdzi, czego brakowało.
 */
export function czyMoznaWygasicGlobalnie(caps: readonly Capability[] = CAPABILITIES): boolean {
  return blokadyWygaszenia(caps).length === 0;
}
